/**
 * One hub row per comparison (compare-surfaces spec §4.3, §12.9). A paired
 * Compare surfaces submit starts two ordinary batches — cut & fill and
 * deviation — that share a `groupId`; the hub shows them as ONE row. Pure: no
 * React, no i18n.
 */
import { ProcessJobStatus, ProcessJobType } from '@/graphql/graphql';

import type { CompareResult } from './compare/availability';
import { EMPTY_RUN_FILES, isRunInProgress, toDate, type AnalysisRunFileItem, type RunFiles } from './runList';
import type { AnalysisRunItem } from './runs';

/** A run's status, plus "Partly failed" for a comparison with mixed outcomes. */
export type RunDisplayStatus = ProcessJobStatus | 'PARTIAL';

/**
 * A hub row. An ungrouped run is a row with one part (itself). A comparison's
 * row carries its primary part's identity (`id`, `batchId`, `type`, `name`,
 * `resultLayerId`: the cut & fill part) and aggregate times, inputs and
 * status:
 *  - `displayStatus` is what the row shows ("Partly failed" included);
 *  - `status` stays a real `ProcessJobStatus` for the code that reads it
 *    (filters, the active-run poll): PARTIAL is FAILED there, since the
 *    comparison is over and something in it failed;
 *  - `error` is null — each part keeps its own, and the UI names the part.
 */
export type MergedRun = AnalysisRunItem & {
  parts: AnalysisRunItem[];
  displayStatus: RunDisplayStatus;
};

/** The Compare surfaces result a job type produces, or null. */
export function comparePartOf(type: ProcessJobType | string): CompareResult | null {
  if (type === ProcessJobType.SurfaceDifference) return 'cutFill';
  if (type === ProcessJobType.Deviation) return 'deviation';
  return null;
}

const isUnfinished = (s: ProcessJobStatus) => s === ProcessJobStatus.Waiting || s === ProcessJobStatus.Processing;

/**
 * Any part still queued or running → PROCESSING (WAITING while none has
 * started); all processed → PROCESSED; all failed → FAILED; otherwise
 * PARTIAL.
 */
export function mergedDisplayStatus(parts: readonly Pick<AnalysisRunItem, 'status'>[]): RunDisplayStatus {
  const statuses = parts.map((p) => p.status);
  if (statuses.some(isUnfinished)) {
    return statuses.every((s) => s === ProcessJobStatus.Waiting) ? ProcessJobStatus.Waiting : ProcessJobStatus.Processing;
  }
  if (statuses.every((s) => s === ProcessJobStatus.Processed)) return ProcessJobStatus.Processed;
  if (statuses.every((s) => s === ProcessJobStatus.Failed)) return ProcessJobStatus.Failed;
  return 'PARTIAL';
}

const timeOf = (value: unknown): number => {
  const ms = toDate(value).getTime();
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
};

/** Cut & fill first, then deviation, then anything else (stable). */
const partRank = (run: AnalysisRunItem): number => {
  const part = comparePartOf(run.type);
  return part === 'cutFill' ? 0 : part === 'deviation' ? 1 : 2;
};

const single = (run: AnalysisRunItem): MergedRun => ({ ...run, parts: [run], displayStatus: run.status });

function mergeGroup(group: AnalysisRunItem[]): MergedRun {
  if (group.length === 1) return single(group[0]);
  const parts = [...group].sort((a, b) => partRank(a) - partRank(b));
  const primary = parts[0];
  const displayStatus = mergedDisplayStatus(parts);
  const earliest = parts.reduce((a, b) => (timeOf(b.createdAt) < timeOf(a.createdAt) ? b : a));
  const finished = parts.every((p) => p.finishedAt);
  const latestFinish = finished
    ? parts.reduce((a, b) => (timeOf(b.finishedAt) > timeOf(a.finishedAt) ? b : a)).finishedAt
    : null;
  return {
    ...primary,
    createdAt: earliest.createdAt,
    finishedAt: latestFinish,
    inputLayerIds: [...new Set(parts.flatMap((p) => p.inputLayerIds))],
    error: null,
    status: displayStatus === 'PARTIAL' ? ProcessJobStatus.Failed : displayStatus,
    displayStatus,
    parts,
  };
}

/**
 * The hub's rows: runs sharing a `groupId` become one row, every other run
 * passes through. A comparison takes the place of its first part in `runs`;
 * the server lists runs newest first, so that is its newest part — rows stay
 * ordered newest first without re-sorting the server's order.
 */
export function mergeRuns(runs: readonly AnalysisRunItem[]): MergedRun[] {
  const groups = new Map<string, AnalysisRunItem[]>();
  const entries: AnalysisRunItem[][] = [];
  for (const run of runs) {
    if (!run.groupId) {
      entries.push([run]);
      continue;
    }
    let parts = groups.get(run.groupId);
    if (!parts) {
      parts = [];
      groups.set(run.groupId, parts);
      entries.push(parts);
    }
    parts.push(run);
  }
  return entries.map(mergeGroup);
}

/** A row that stands for a paired comparison (two or more parts). */
export const isComparison = (run: Pick<MergedRun, 'parts'>): boolean => run.parts.length > 1;

/** A row with a part still queued or running. Its row / drawer delete stays
 *  available — a stuck run must remain deletable, no sweeper exists (ruling
 *  R26) — but the confirmation warns that it is still running. */
export const isDeleteWarned = (run: Pick<MergedRun, 'parts'>): boolean => run.parts.some(isRunInProgress);

/** Every batch behind a row — what Delete and bulk selection act on. */
export const mergedRunBatchIds = (run: Pick<MergedRun, 'parts'>): string[] =>
  run.parts.map((p) => p.batchId).filter((id): id is string => !!id);

/**
 * A row's downloads: every part's files, newest first. A combined report is
 * linked to both parts — it appears once. A single-part row returns its own
 * entry unchanged (keeps the list rows' memo stable).
 */
export function mergeRunFiles(run: Pick<MergedRun, 'id' | 'parts'>, byRun: ReadonlyMap<string, RunFiles> | undefined): RunFiles {
  if (run.parts.length <= 1) return byRun?.get(run.id) ?? EMPTY_RUN_FILES;
  const seen = new Set<string>();
  const pick = (list: (f: RunFiles) => AnalysisRunFileItem[]) =>
    run.parts
      .flatMap((p) => list(byRun?.get(p.id) ?? EMPTY_RUN_FILES))
      .filter((f) => (seen.has(f.id) ? false : (seen.add(f.id), true)))
      .sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt));
  const pdfs = pick((f) => f.pdfs);
  const others = pick((f) => f.others);
  return pdfs.length === 0 && others.length === 0 ? EMPTY_RUN_FILES : { pdfs, others };
}
